import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MessageBar } from '@/components/atoms/MessageBar/MessageBar';
import { RadioGroup } from '@/components/atoms/RadioSwitch';
import { RadioInput } from '@/components/atoms/RadioSwitch/RadioInput';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import {
  formFields,
  formValues,
  FormValues,
} from '@/containers/ConfigurePage/components/FormConfig/formData';
import { useData } from '@/lib/pageData/pageDataContext';
import { TransformedAssetCategoryResult } from '@/types/TransformedAssetCategoryResults';
import { ProductSubscriptionByRegionResponse } from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { formatPrice } from '@/utils/formatPrice';
import { useStudentDiscount } from '../../hooks/useStudentDiscount';
import styles from './AreYouStudent.module.scss';

interface Props {
  product: TransformedAssetCategoryResult;
  subscriptions: ProductSubscriptionByRegionResponse;
}

export const AreYouStudent = ({ product, subscriptions }: Props) => {
  const { currency } = useData<'default'>();
  const { locale } = useRouter();
  const { control } = useFormContext<FormValues>();
  const { t } = useTranslation(nameSpacesConfig.pages);
  const { student } = useWatch<FormValues>({ control });
  const discount = useStudentDiscount(product, subscriptions);

  const discountPrice = discount
    ? Math.round((discount.normalRate - discount.studentRate) * 100) / 100
    : undefined;
  const formattedDiscountPrice = discountPrice
    ? formatPrice(discountPrice, locale!, currency, discountPrice % 1 !== 0)
    : undefined;

  return (
    <Controller
      control={control}
      name={formFields.student}
      render={({ field }) => (
        <RadioGroup
          data-test-id="student-radio-group"
          label={t('configure.student.label')}
          version="level3"
          onChange={field.onChange}
          value={field.value}
          name={field.name}
        >
          <div className={styles.group}>
            {formattedDiscountPrice && (
              <span className={styles.sublabel} translate="no">
                {t('configure.student.discount.sublabel', {
                  price: formattedDiscountPrice,
                })}
              </span>
            )}
            <RadioInput
              aria-label={t('configure.student.button1')}
              value={formValues.student.yes}
              className={styles.button}
            >
              <div className={styles.radioText}>
                <span>{t('configure.student.button1')}</span>
                {formattedDiscountPrice && (
                  <span translate="no">
                    {t('configure.student.discount.radioLabel', {
                      price: formattedDiscountPrice,
                    })}
                  </span>
                )}
              </div>
            </RadioInput>
            <RadioInput
              aria-label={t('configure.student.button2')}
              value={formValues.student.no}
              className={styles.button}
            >
              <span>{t('configure.student.button2')}</span>
            </RadioInput>
            {student == 'yes' && discountPrice && (
              <MessageBar className={styles.discountInfo}>
                {t('configure.student.discount.notificationText')}
              </MessageBar>
            )}
          </div>
        </RadioGroup>
      )}
    />
  );
};
